import { QuestionOutlined } from "@ant-design/icons"
import { App } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import {
  ARCHIVE_ACKNOWLEDGEMENT,
  UNPAID_INVOICES_ACKNOWLEDGEMENT,
  commitmentsFromRejection,
  unpaidInvoicesFromRejection
} from "../../../Projects/api/pending-commitments.ts"
import {
  type ClosingStatus,
  UnpaidInvoicesDialog
} from "../../../Projects/components/UnpaidInvoicesDialog.tsx"
import { isArchivingStatus } from "../../../Projects/constants/project-statuses.ts"
import {
  StatusHistoryPopover,
  type StatusUndoResult
} from "../../../StatusAutomation/components/StatusHistoryPopover.tsx"
import { StatusSuggestionChips } from "../../../StatusAutomation/components/StatusSuggestionChips.tsx"
import { useRefreshAfterAutomaticMove } from "../../../StatusAutomation/hooks/use-refresh-after-automatic-move.ts"
import {
  readStatusRefusal,
  refusalMessage,
  statusDisplay
} from "../../../StatusAutomation/helpers/status-automation.ts"
import type {
  StatusSuggestion,
  StatusTransition
} from "../../../StatusAutomation/types/status-automation.types.ts"
import { confirmProjectCommitments } from "../../helpers/confirm-project-commitments.tsx"
import {
  acceptProjectStatusSuggestion,
  dismissProjectStatusSuggestion,
  refreshProjectStatus,
  undoProjectStatusTransition
} from "../../redux/project-status-automation.thunks.ts"
import { fetchProjectLogHistory } from "../../redux/project.thunks.ts"
import type { Project } from "../../types/project.types.ts"

interface ProjectStatusRights {
  /** `project_change_status` on an editable project. */
  canChangeStatus: boolean
  /** `project_close` as well: Completed archives the project. */
  canClose: boolean
}

/** The right the move itself would take, as the status selector decides it. */
export const canAnswerProjectSuggestion = (
  suggestion: Pick<StatusSuggestion, "target">,
  { canChangeStatus, canClose }: ProjectStatusRights
) =>
  suggestion.target !== null && isArchivingStatus(suggestion.target)
    ? canClose
    : canChangeStatus

interface ProjectStatusSuggestionsProps extends ProjectStatusRights {
  project: Project
  canViewLogs: boolean
}

export function ProjectStatusSuggestions({
  project,
  canChangeStatus,
  canClose,
  canViewLogs
}: ProjectStatusSuggestionsProps) {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const dispatch = useAppDispatch()
  const [busyId, setBusyId] = useState<string | null>(null)
  // Set when the server asks about unpaid invoices before the close.
  const [unpaid, setUnpaid] = useState<{
    suggestion: StatusSuggestion
    count: number
    meetings: boolean
  } | null>(null)

  const afterMove = () => {
    if (canViewLogs) void dispatch(fetchProjectLogHistory(project.id))
  }

  // A rule runs on the server after the write that set it off; the header
  // learns of the move here, not on the next reload.
  useRefreshAfterAutomaticMove(project.id, () => {
    void dispatch(refreshProjectStatus(project.id))
  })

  // Each answer is kept and resent, so a close that asks about the money and
  // then the meetings carries both acknowledgements.
  const send = async (
    suggestion: StatusSuggestion,
    acknowledged = false,
    unpaidAcknowledged = false
  ) => {
    setBusyId(suggestion.id)
    const body = {
      ...(acknowledged ? ARCHIVE_ACKNOWLEDGEMENT : {}),
      ...(unpaidAcknowledged ? UNPAID_INVOICES_ACKNOWLEDGEMENT : {})
    }
    try {
      await dispatch(
        acceptProjectStatusSuggestion({
          projectId: project.id,
          suggestionId: suggestion.id,
          ...(Object.keys(body).length ? { body } : {})
        })
      ).unwrap()
      setUnpaid(null)
      message.success(
        suggestion.target === null
          ? t("statusAutomation.toasts.acknowledged")
          : t("statusAutomation.toasts.moved", {
              status: statusDisplay("project", suggestion.target, t)
            })
      )
      afterMove()
    } catch (rejection) {
      const refusal = readStatusRefusal(rejection)
      if (refusal) {
        message.warning(refusalMessage(refusal, t))
        return
      }
      const owed = unpaidInvoicesFromRejection(rejection)
      if (owed) {
        setUnpaid({
          suggestion,
          count: owed.unpaidInvoiceCount,
          meetings: acknowledged
        })
        return
      }
      setUnpaid(null)
      const question = commitmentsFromRejection(rejection)
      if (question) {
        confirmProjectCommitments({
          modal,
          t,
          question,
          onOk: () => send(suggestion, true, unpaidAcknowledged)
        })
      }
      // A missing prerequisite was already reported.
    } finally {
      setBusyId(null)
    }
  }

  const accept = (suggestion: StatusSuggestion) => {
    if (suggestion.target === null || !isArchivingStatus(suggestion.target)) {
      void send(suggestion)
      return
    }
    // Completed archives the project, so it is confirmed as the selector does.
    modal.confirm({
      centered: true,
      icon: <QuestionOutlined />,
      title: t("statusAutomation.closeConfirm.title", {
        status: statusDisplay("project", suggestion.target, t)
      }),
      content: t("statusAutomation.closeConfirm.body"),
      okText: t("statusAutomation.chip.moveAction"),
      cancelText: t("statusAutomation.closeConfirm.cancel"),
      onOk: () => send(suggestion)
    })
  }

  const dismiss = async (suggestion: StatusSuggestion) => {
    setBusyId(suggestion.id)
    try {
      await dispatch(
        dismissProjectStatusSuggestion({
          projectId: project.id,
          suggestionId: suggestion.id
        })
      ).unwrap()
      message.success(t("statusAutomation.toasts.dismissed"))
    } catch (rejection) {
      const refusal = readStatusRefusal(rejection)
      if (refusal) message.warning(refusalMessage(refusal, t))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <StatusSuggestionChips
        kind={"project"}
        recordId={project.id}
        suggestions={project.status_suggestions}
        canAct={(suggestion) =>
          canAnswerProjectSuggestion(suggestion, { canChangeStatus, canClose })
        }
        busyId={busyId}
        onAccept={accept}
        onDismiss={(suggestion) => void dismiss(suggestion)}
      />
      {unpaid && unpaid.suggestion.target !== null && (
        <UnpaidInvoicesDialog
          open
          mode="close"
          count={unpaid.count}
          status={unpaid.suggestion.target as ClosingStatus}
          busy={busyId === unpaid.suggestion.id}
          onClose={() => setUnpaid(null)}
          onConfirm={() => void send(unpaid.suggestion, unpaid.meetings, true)}
        />
      )}
    </>
  )
}

interface ProjectStatusHistoryProps {
  projectId: string
  canChangeStatus: boolean
  canViewLogs: boolean
}

export function ProjectStatusHistory({
  projectId,
  canChangeStatus,
  canViewLogs
}: ProjectStatusHistoryProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const dispatch = useAppDispatch()

  const undo = async (
    transition: StatusTransition
  ): Promise<StatusUndoResult> => {
    try {
      const answer = await dispatch(
        undoProjectStatusTransition({ projectId, transitionId: transition.id })
      ).unwrap()
      message.success(t("statusAutomation.toasts.undone"))
      if (canViewLogs) void dispatch(fetchProjectLogHistory(projectId))
      return { ok: true, transitions: answer?.status_transitions }
    } catch (rejection) {
      return { ok: false, refusal: readStatusRefusal(rejection) }
    }
  }

  return (
    <StatusHistoryPopover
      kind={"project"}
      recordId={projectId}
      canUndo={canChangeStatus}
      onUndo={undo}
    />
  )
}
